import { resolveGameConfig } from "./config.js";
import { hashGameState } from "./hash.js";
import { cloneGameState, stepGame } from "./simulation.js";
import { PHYSICS_VERSION, PROTOCOL_VERSION, } from "./types.js";
function cloneInput(input) {
    const value = {
        player: input.player,
        sequence: input.sequence,
        targetX: input.targetX,
        targetY: input.targetY,
    };
    return input.clientTime === undefined ? value : { ...value, clientTime: input.clientTime };
}
function cloneFrame(frame) {
    return {
        tick: frame.tick,
        inputs: frame.inputs.map(cloneInput),
        stateHash: frame.stateHash,
    };
}
export function createReplayRecord(initialState, configInput = {}) {
    const config = resolveGameConfig(configInput);
    const state = cloneGameState(initialState);
    const initialStateHash = hashGameState(state);
    return {
        physicsVersion: PHYSICS_VERSION,
        protocolVersion: PROTOCOL_VERSION,
        config,
        initialState: state,
        initialStateHash,
        frames: [],
        finalStateHash: initialStateHash,
    };
}
/** Returns a new record, so persistence code can safely retain older checkpoints. */
export function appendReplayFrame(record, result) {
    const frame = {
        tick: result.state.tick,
        inputs: result.acceptedInputs.map(cloneInput),
        stateHash: result.stateHash,
    };
    return {
        ...record,
        config: resolveGameConfig(record.config),
        initialState: cloneGameState(record.initialState),
        frames: [...record.frames.map(cloneFrame), frame],
        finalStateHash: result.stateHash,
    };
}
export class ReplayDivergenceError extends Error {
    tick;
    expectedHash;
    actualHash;
    constructor(tick, expectedHash, actualHash) {
        super(`Replay diverged at tick ${tick}: expected ${expectedHash}, received ${actualHash}`);
        this.name = "ReplayDivergenceError";
        this.tick = tick;
        this.expectedHash = expectedHash;
        this.actualHash = actualHash;
    }
}
export function playReplay(record) {
    if (record.physicsVersion !== PHYSICS_VERSION || record.protocolVersion !== PROTOCOL_VERSION) {
        throw new RangeError("Replay version does not match this game-core build");
    }
    const initialHash = hashGameState(record.initialState);
    if (initialHash !== record.initialStateHash) {
        throw new ReplayDivergenceError(record.initialState.tick, record.initialStateHash, initialHash);
    }
    let state = cloneGameState(record.initialState);
    for (const frame of record.frames) {
        if (frame.tick !== state.tick + 1) {
            throw new RangeError(`Replay frame tick ${frame.tick} is not contiguous after ${state.tick}`);
        }
        const result = stepGame(state, frame.inputs, record.config);
        if (result.stateHash !== frame.stateHash) {
            throw new ReplayDivergenceError(frame.tick, frame.stateHash, result.stateHash);
        }
        state = result.state;
    }
    const finalStateHash = hashGameState(state);
    if (finalStateHash !== record.finalStateHash) {
        throw new ReplayDivergenceError(state.tick, record.finalStateHash, finalStateHash);
    }
    return { state, finalStateHash, verifiedFrames: record.frames.length };
}
//# sourceMappingURL=replay.js.map